import { MAX_MONTHS_TO_DISPLAY } from "@booking/booking/api/calendar/constants";
import { EPOCH_DATE } from "@search/store/constants";
import clsx from "clsx";
import { addMonths, isBefore, startOfMonth } from "date-fns";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import "react-day-picker/style.css";

import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./DatePickerFacet.module.css";

const ref = "date-picker";

interface DatePickerFacetProps extends Partial<CommonProps> {
  date?: Date;
  update: (date: Date) => void;
}

function useDatePickerFacet(props: DatePickerFacetProps) {
  const today = new Date();
  const { color = ColorEnum.DARKGREEN, date, id = createId(ref) } = props;
  const className = clsx(color && styles[color], props.className);

  const defaultClassNames = getDefaultClassNames();

  return {
    className,
    classNames: {
      month_caption: clsx(
        defaultClassNames.month_caption,
        styles["month-caption"],
      ),
      root: clsx(defaultClassNames.root, styles[ref]),
      selected: clsx(defaultClassNames.selected, styles.selected),
    },
    date: date && isBefore(date, startOfMonth(today)) ? undefined : date,
    disabledDates: { before: today },
    endMonth: addMonths(today, MAX_MONTHS_TO_DISPLAY),
    id,
    startMomth: today,
  };
}

function DatePickerFacet(props: DatePickerFacetProps) {
  const {
    className,
    classNames,
    date,
    disabledDates,
    endMonth,
    id,
    startMomth,
  } = useDatePickerFacet(props);

  const handleSelect = (date: Date | undefined) => {
    props.update(date || EPOCH_DATE);
  };

  return (
    <section className={className} id={id}>
      <DayPicker
        classNames={classNames}
        defaultMonth={date}
        disabled={disabledDates}
        endMonth={endMonth}
        fixedWeeks
        mode="single"
        selected={date}
        showOutsideDays
        startMonth={startMomth}
        onSelect={handleSelect}
      />
    </section>
  );
}

export default DatePickerFacet;
export { DatePickerFacet, type DatePickerFacetProps };
